import React, { FC, useEffect } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Controller, useForm } from "react-hook-form";
import Button from "@/components/Button";
import { OrganizationLabel } from "@/domain/organization-labels.interface";
import { OrganizationLabelPayload } from "@/services/organizationLabels.service";

export type OrganizationLabelFormData = {
  name: string;
  description: string;
  status: "ACTIVE" | "INACTIVE";
};

type OrganizationLabelModalProps = {
  open: boolean;
  onClose: () => void;
  organizationLabel?: OrganizationLabel;
  onSave: (data: OrganizationLabelPayload) => void;
  isPending: boolean;
};

const OrganizationLabelModal: FC<OrganizationLabelModalProps> = ({
  open,
  onClose,
  organizationLabel,
  onSave,
  isPending,
}) => {
  const theme = useAppTheme();
  const isEdit = Boolean(organizationLabel);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OrganizationLabelFormData>({
    mode: "all",
    defaultValues: {
      name: "",
      description: "",
      status: "ACTIVE",
    },
  });

  useEffect(() => {
    if (organizationLabel) {
      reset({
        name: organizationLabel.name || "",
        description: organizationLabel.description || "",
        status: (organizationLabel.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
      });
    } else {
      reset({
        name: "",
        description: "",
        status: "ACTIVE",
      });
    }
  }, [organizationLabel, reset]);

  const onSubmit = (data: OrganizationLabelFormData) => {
    onSave({
      name: data.name.trim(),
      description: data.description.trim(),
      status: data.status,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEdit ? "Edit Organization Label" : "Create Organization Label"}
      size={{
        width: "100%",
        minHeight: 420,
        maxHeight: 560,
        maxWidth: 560,
      }}
      content={
        <Box sx={{ width: "100%", padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: "Name is required",
                  maxLength: {
                    value: 100,
                    message: "Name must be 100 characters or less",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Name"
                    required
                    size="medium"
                    error={!!errors.name}
                    helperText={errors.name?.message || ""}
                    disabled={isPending}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                rules={{
                  required: "Description is required",
                  maxLength: {
                    value: 500,
                    message: "Description must be 500 characters or less",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description"
                    required
                    multiline
                    minRows={3}
                    maxRows={5}
                    error={!!errors.description}
                    helperText={errors.description?.message || ""}
                    disabled={isPending}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="status"
                control={control}
                rules={{ required: "Status is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Status"
                    required
                    error={!!errors.status}
                    helperText={errors.status?.message || ""}
                    disabled={isPending}
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default OrganizationLabelModal;
